import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import { useInventory } from "../../inventory/InventoryContext";
import { useAuth, isSuperAdmin } from "../../contexts/AuthContext";
import { usePlans } from "../../plans/PlansContext";
import { formatMoney, formatReach, parseMoney } from "../../inventory/parse";
import { formatDateTime, timeAgo } from "../../inventory/time";
import { ACTION_LABELS } from "../../inventory/activity";
import Drawer from "../../components/ui/Drawer";
import Icon from "../../components/ui/Icon";
import AddToPlan from "./AddToPlan";
import "../plans/Plans.css";

const FORMAT_TONE = { ctv: "blue", hbbtv: "green", broadcast: "yellow" };

function Chips({ values, tone }) {
  if (!values?.length) return <span className="muted">—</span>;
  return (
    <div className="cell-chips">
      {values.map((v) => (
        <span key={v} className={`chip chip-${tone ? tone(v) : "gray"}`}>
          {v}
        </span>
      ))}
    </div>
  );
}

// CPM over time as a small step line, oldest on the left
function PriceLine({ points }) {
  if (points.length < 2) return null;
  const w = 380;
  const h = 56;
  const values = points.map((p) => p.value);
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const x = (i) => 6 + (i / (points.length - 1)) * (w - 12);
  const y = (v) => h - 8 - ((v - min) / span) * (h - 16);
  let d = `M${x(0)},${y(values[0])}`;
  values.slice(1).forEach((v, i) => {
    d += ` H${x(i + 1)} V${y(v)}`;
  });
  return (
    <svg className="price-line" viewBox={`0 0 ${w} ${h}`} role="img" aria-label="CPM over time">
      <path d={d} fill="none" stroke="var(--brand-blue)" strokeWidth="2" />
      {values.map((v, i) => (
        <circle key={i} cx={x(i)} cy={y(v)} r="3" fill="var(--bg-card)" stroke="var(--brand-blue)" strokeWidth="2" />
      ))}
    </svg>
  );
}

export default function ChannelDrawer({ channel, canEdit, onClose, onEdit, onDuplicate, onDelete, onAddedToPlan }) {
  const { activity } = useInventory();
  // Price and change history are for superadmins only
  const withHistory = isSuperAdmin(useAuth().user);
  const { plans } = usePlans();

  const history = useMemo(
    () => activity.filter((e) => (e.channels || []).some((c) => c.id === channel.id)),
    [activity, channel.id]
  );

  // Every recorded CPM change, oldest first, then today's price
  const prices = useMemo(() => {
    const changes = history
      .filter((e) => e.action === "edit")
      .flatMap((e) => (e.changes || []).filter((c) => c.key === "cpm").map((c) => ({ ...c, at: e.at, user: e.user })))
      .reverse();
    const points = [];
    if (changes.length) {
      const first = parseMoney(changes[0].from).amount;
      if (first != null) points.push({ value: first });
      changes.forEach((c) => {
        const v = parseMoney(c.to).amount;
        if (v != null) points.push({ value: v });
      });
    }
    return { changes, points };
  }, [history]);

  const inPlans = plans.filter((p) => p.items.some((i) => i.channelId === channel.id));

  return (
    <Drawer
      title={channel.channelName}
      subtitle={[channel.partner, channel.platform].filter(Boolean).join(" · ")}
      onClose={onClose}
      footer={
        <>
          <AddToPlan channels={[channel]} onAdded={onAddedToPlan} triggerClass="btn btn-accent" />
          <span className="spacer" />
          {canEdit && (
            <>
              <button type="button" className="icon-btn" title="Duplicate" aria-label="Duplicate" onClick={() => onDuplicate(channel)}>
                <Icon name="copy" />
              </button>
              <button type="button" className="icon-btn danger" title="Delete" aria-label="Delete" onClick={() => onDelete(channel)}>
                <Icon name="trash" />
              </button>
              <button type="button" className="btn-primary" onClick={() => onEdit(channel)}>
                <Icon name="edit" /> Edit
              </button>
            </>
          )}
        </>
      }
    >
      <section className="dr-section">
        <div className="dr-figures">
          <div>
            <span>CPM</span>
            <strong>{channel.cpm == null ? "—" : formatMoney(channel.cpm, channel.currency)}</strong>
          </div>
          <div>
            <span>Reach</span>
            <strong>{formatReach(channel.reach) || "—"}</strong>
          </div>
          <div>
            <span>Revshare</span>
            <strong>{channel.revshare || "—"}</strong>
          </div>
        </div>
      </section>

      <section className="dr-section">
        <dl className="dr-list">
          <dt>Territory</dt>
          <dd><Chips values={channel.territories} /></dd>
          <dt>Format</dt>
          <dd><Chips values={channel.formats} tone={(v) => FORMAT_TONE[v.toLowerCase()] || "gray"} /></dd>
          <dt>Ad format</dt>
          <dd><Chips values={channel.adFormats} /></dd>
          <dt>Ad type</dt>
          <dd><Chips values={channel.adTypes} /></dd>
          <dt>Genre</dt>
          <dd><Chips values={channel.genres} /></dd>
          <dt>Commercial model</dt>
          <dd>{channel.revshareType || <span className="muted">—</span>}</dd>
          <dt>Contact</dt>
          <dd>
            {channel.contactName || channel.contactEmail ? (
              <div className="cell-contact">
                <span>{channel.contactName || channel.contactEmail}</span>
                {channel.contactEmail && <a href={`mailto:${channel.contactEmail}`}>{channel.contactEmail}</a>}
              </div>
            ) : (
              <span className="muted">—</span>
            )}
          </dd>
        </dl>
      </section>

      <section className="dr-section">
        <h3>In media plans</h3>
        {inPlans.length ? (
          <ul className="dr-plans">
            {inPlans.map((p) => (
              <li key={p.id}>
                <Link to={`/plans/${p.id}`}>{p.name}</Link>
                <span className={`status status-${p.status.toLowerCase()}`}>{p.status}</span>
              </li>
            ))}
          </ul>
        ) : (
          <p className="dr-empty">Not in any plan yet.</p>
        )}
      </section>

      {withHistory && (
        <section className="dr-section">
          <h3>Price history</h3>
          {prices.changes.length ? (
            <>
              <PriceLine points={prices.points} />
              <ul className="dr-prices">
                {[...prices.changes].reverse().map((c, i) => (
                  <li key={i}>
                    <span className="rc-from">{c.from || "empty"}</span>
                    <Icon name="chevron" size={12} className="rot-270" />
                    <strong>{c.to || "empty"}</strong>
                    <span className="dr-when" title={formatDateTime(c.at)}>
                      {timeAgo(c.at)} · {c.user}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          ) : (
            <p className="dr-empty">No price changes recorded. The current CPM is the only one on file.</p>
          )}
        </section>
      )}

      <section className="dr-section">
        {withHistory && (
          <>
            <h3>
              History
              {history.length > 0 && (
                <Link to={`/recent?channel=${encodeURIComponent(channel.id)}`} className="dr-all">
                  View all
                </Link>
              )}
            </h3>
            {history.length ? (
              <ul className="dr-history">
                {history.slice(0, 8).map((e) => (
                  <li key={e.id}>
                    <span className={`dr-dot tone-${e.action}`} aria-hidden="true" />
                    <div>
                      <div>
                        <strong>{ACTION_LABELS[e.action] || e.action}</strong>
                        {e.action === "edit" && e.changes?.length > 0 && (
                          <span className="dr-what"> {e.changes.map((c) => c.label).join(", ")}</span>
                        )}
                        {e.action === "import" && e.note && <span className="dr-what"> from {e.note}</span>}
                      </div>
                      <div className="dr-when" title={formatDateTime(e.at)}>
                        {timeAgo(e.at)} · {e.user}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="dr-empty">No changes recorded for this channel.</p>
            )}
          </>
        )}
        <p className="dr-meta">
          {channel.createdAt ? (
            <>Created {formatDateTime(channel.createdAt)} by {channel.createdBy}. </>
          ) : (
            <>From the sample data. </>
          )}
          {channel.updatedAt && (
            <>
              Last updated {formatDateTime(channel.updatedAt)}
              {channel.updatedBy && <> by {channel.updatedBy}</>}.
            </>
          )}
        </p>
      </section>
    </Drawer>
  );
}
